import { useSearchParams } from 'react-router';

import { deployEnvironment } from '../schemas/deploy-target';
import { readTarget, type ClientSlot, type ClientTarget } from '../schemas/target';

/**
 * The environment scope, held in the URL.
 *
 * ## Why the URL and not a context
 *
 * Which backend you are looking at is the single most important fact on the
 * screen, and it survives a reload, a bookmark and a link pasted into Slack
 * only if it is addressable. A provider would lose it on every refresh and
 * quietly reset to the default — which, for a screen that also *writes*, means
 * an operator who reloaded on production could create a client in dev without
 * noticing anything changed.
 *
 * ## Why it is NOT in the app-wide topbar
 *
 * It was, in the prototype, where clients were the only screens. In this app
 * the topbar is shared with Feature Flags, which has an environment concept of
 * its own and a completely different shape — one row in one table, four values
 * compared side by side. A global switcher would read as scoping that too. So
 * this belongs to the clients screens and travels in their URL.
 */

export interface ClientTargetState {
    readonly target: ClientTarget;
    readonly setEnvironment: (env: ClientTarget['env']) => void;
    readonly setSlot: (slot: ClientSlot) => void;
    /** `eph` with no slot chosen: there is nothing to address yet. */
    readonly isSlotMissing: boolean;
}

export function useClientTarget(): ClientTargetState {
    const [params, setParams] = useSearchParams();
    const target = readTarget(params, deployEnvironment());

    const write = (next: { env?: ClientTarget['env']; slot?: string }) => {
        setParams(
            (current) => {
                const updated = new URLSearchParams(current);
                if (next.env !== undefined) updated.set('env', next.env);
                if (next.slot !== undefined) {
                    if (next.slot) updated.set('slot', next.slot);
                    else updated.delete('slot');
                }
                return updated;
            },
            { replace: true },
        );
    };

    return {
        target,
        isSlotMissing: target.env === 'eph' && target.slot === undefined,

        setEnvironment: (env) => {
            // Leaving `eph` and coming back must not inherit the old slot: it
            // is not part of any other environment's address, and carrying it
            // would put a stale one back the moment `eph` is picked again.
            write({ env, ...(env === 'eph' ? {} : { slot: '' }) });
        },
        setSlot: (slot) => {
            write({ slot });
        },
    };
}
